import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import TourEngine from "./TourEngine";
import { TOURS, TAB_TOURS, MODULE_LABELS, SUGGESTIONS, HOVER_TIPS } from "./tours";
import { GraduationCap, Sparkles, CheckCircle2, PlayCircle, Compass, X, Lightbulb, Bot } from "lucide-react";

const Ctx = createContext(null);
export const useOnboarding = () => useContext(Ctx);

export function OnboardingProvider({ children, onNavigateTab }) {
    const [state, setState] = useState(null);
    const [active, setActive] = useState(null);
    const [resumeOffer, setResumeOffer] = useState(null);
    const [suggestion, setSuggestion] = useState(null); // {id, tab, text}
    const [helpOffer, setHelpOffer] = useState(null); // {tourId, label}
    const autoStarted = useRef(new Set());
    const suggestedThisSession = useRef(false);
    const helpOfferedTabs = useRef(new Set());
    const currentTabRef = useRef("panoramica");
    const stateRef = useRef(null);
    const activeRef = useRef(null);
    stateRef.current = state;
    activeRef.current = active;

    useEffect(() => {
        api.get("/onboarding/state").then((r) => {
            setState(r.data);
            const paused = Object.entries(r.data.tours || {}).find(([, t]) => t.status === "in_progress" && t.step > 0);
            if (paused && r.data.welcomed) setResumeOffer(paused[0]);
        }).catch(() => setState({ welcomed: true, mode: "explore", tours: {} }));
    }, []);

    const saveTour = useCallback((id, status, step) => {
        const total = TOURS[id].steps.length;
        const mapped = status === "paused" ? "in_progress" : status;
        setState((s) => ({ ...s, tours: { ...s.tours, [id]: { status: mapped, step, total } } }));
        api.put(`/onboarding/tours/${id}`, { status: mapped, step, total }).catch(() => {});
    }, []);

    const startTour = useCallback((id, fromStep = 0) => {
        if (!TOURS[id]) return;
        setResumeOffer(null); setSuggestion(null); setHelpOffer(null);
        setActive({ id, fromStep });
        saveTour(id, "paused", fromStep);
    }, [saveTour]);

    const navigateAndStart = useCallback((id) => {
        const tab = Object.keys(TAB_TOURS).find((k) => TAB_TOURS[k] === id);
        if (tab) onNavigateTab?.(tab);
        setTimeout(() => startTour(id, 0), tab ? 500 : 0);
    }, [onNavigateTab, startTour]);

    const maybeAutoStart = useCallback((tourId) => {
        const s = stateRef.current;
        if (!s || !s.welcomed || s.mode !== "tour" || !tourId || activeRef.current) return;
        if (autoStarted.current.has(tourId)) return;
        if ((s.tours || {})[tourId]) return;
        autoStarted.current.add(tourId);
        setTimeout(() => startTour(tourId, 0), 600);
    }, [startTour]);

    // ---- FASE 2: tracking visite + suggerimenti proattivi ----
    const trackVisit = useCallback((tab) => {
        currentTabRef.current = tab;
        api.post("/onboarding/visit", { tab }).then((r) => {
            setState((s) => (s ? { ...s, visits: r.data.visits, total_visits: r.data.total_visits } : s));
        }).catch(() => {});
    }, []);

    // suggerimento per modulo mai esplorato (dopo >=3 visite totali, max 1 per sessione, dopo 18s)
    useEffect(() => {
        if (!state || !state.welcomed || suggestedThisSession.current) return;
        if ((state.total_visits || 0) < 3) return;
        const t = setTimeout(() => {
            const s = stateRef.current;
            if (!s || activeRef.current || suggestedThisSession.current) return;
            const candidate = Object.entries(SUGGESTIONS).find(([id, sg]) => {
                const st = ((s.suggestions || {})[id] || {}).status;
                if (st === "accepted" || st === "dismissed") return false;
                if ((s.tours || {})[TAB_TOURS[sg.tab]]) return false;
                if (((s.visits || {})[sg.tab] || 0) > 0) return false;
                return true;
            });
            if (candidate) {
                suggestedThisSession.current = true;
                setSuggestion({ id: candidate[0], ...candidate[1] });
                api.put(`/onboarding/suggestions/${candidate[0]}`, { status: "shown" }).catch(() => {});
            }
        }, 18000);
        return () => clearTimeout(t);
    }, [state]);

    // euristica di difficoltà: 45s senza interazioni su un tab col tour non completato → offri aiuto
    useEffect(() => {
        if (!state || !state.welcomed) return;
        let timer = null;
        const arm = () => {
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => {
                const s = stateRef.current;
                const tab = currentTabRef.current;
                const tourId = TAB_TOURS[tab];
                if (!s || activeRef.current || !tourId || helpOfferedTabs.current.has(tab)) return;
                const t = (s.tours || {})[tourId];
                if (t && (t.status === "completed" || t.status === "dismissed")) return;
                helpOfferedTabs.current.add(tab);
                setHelpOffer({ tourId, label: MODULE_LABELS[tourId] });
            }, 45000);
        };
        const reset = () => { setHelpOffer((h) => h); arm(); };
        arm();
        window.addEventListener("click", reset, true);
        window.addEventListener("keydown", reset, true);
        window.addEventListener("scroll", reset, true);
        return () => {
            if (timer) clearTimeout(timer);
            window.removeEventListener("click", reset, true);
            window.removeEventListener("keydown", reset, true);
            window.removeEventListener("scroll", reset, true);
        };
    }, [state]);

    const acceptSuggestion = () => {
        api.put(`/onboarding/suggestions/${suggestion.id}`, { status: "accepted" }).catch(() => {});
        const tab = suggestion.tab;
        setSuggestion(null);
        onNavigateTab?.(tab);
        setTimeout(() => startTour(TAB_TOURS[tab], 0), 700);
    };
    const laterSuggestion = (final) => {
        api.put(`/onboarding/suggestions/${suggestion.id}`, { status: final ? "dismissed" : "later" }).catch(() => {});
        setSuggestion(null);
    };

    const chooseMode = (mode) => {
        setState((s) => ({ ...s, welcomed: true, mode }));
        api.put("/onboarding/state", { welcomed: true, mode }).catch(() => {});
        if (mode === "tour") setTimeout(() => startTour("analytics", 0), 400);
    };

    if (!state) return <>{children}</>;

    return (
        <Ctx.Provider value={{ state, startTour, navigateAndStart, maybeAutoStart, trackVisit }}>
            {children}
            <HoverTips disabled={!!active} />
            {!state.welcomed && <WelcomeModal onChoose={chooseMode} />}
            {state.welcomed && resumeOffer && !active && (
                <ResumeBanner
                    tourId={resumeOffer}
                    onResume={() => startTour(resumeOffer, (state.tours[resumeOffer] || {}).step || 0)}
                    onDismiss={() => { saveTour(resumeOffer, "dismissed", (state.tours[resumeOffer] || {}).step || 0); setResumeOffer(null); }}
                />
            )}
            {state.welcomed && suggestion && !active && (
                <Toast icon={Lightbulb} testid="onboarding-suggestion" text={suggestion.text}
                    primary={{ label: "Mostrami", testid: "suggestion-show", onClick: acceptSuggestion }}
                    secondary={{ label: "Più tardi", testid: "suggestion-later", onClick: () => laterSuggestion(false) }}
                    onClose={() => laterSuggestion(true)} />
            )}
            {state.welcomed && helpOffer && !suggestion && !active && (
                <Toast icon={Bot} testid="onboarding-help-offer"
                    text={`Ho notato che stai esplorando ${helpOffer.label}. Vuoi che ti mostri il metodo più veloce?`}
                    primary={{ label: "Sì", testid: "help-offer-yes", onClick: () => startTour(helpOffer.tourId, 0) }}
                    secondary={{ label: "Più tardi", testid: "help-offer-later", onClick: () => setHelpOffer(null) }}
                    onClose={() => setHelpOffer(null)} />
            )}
            {active && (
                <TourEngine
                    tour={TOURS[active.id]}
                    tourId={active.id}
                    initialStep={active.fromStep}
                    onProgress={(step) => saveTour(active.id, "paused", step)}
                    onFinish={(status, step) => { saveTour(active.id, status, step); setActive(null); }}
                />
            )}
        </Ctx.Provider>
    );
}

function Toast({ icon: Icon, testid, text, primary, secondary, onClose }) {
    return (
        <div data-testid={testid} className="fixed bottom-5 right-5 z-[10040] max-w-sm bg-ink-950 border p-4 shadow-2xl vd-tip-in" style={{ borderColor: "hsl(var(--gold) / 0.5)" }}>
            <div className="flex items-start gap-3">
                <Icon size={17} className="text-gold shrink-0 mt-0.5" />
                <p className="text-xs text-ink-100 leading-relaxed flex-1">{text}</p>
                <button onClick={onClose} className="text-ink-500 hover:text-ink-100 -mt-1"><X size={14} /></button>
            </div>
            <div className="flex gap-2 mt-3 pl-8">
                <button data-testid={primary.testid} onClick={primary.onClick} className="cta-gold px-4 py-1.5 text-[11px]">{primary.label}</button>
                <button data-testid={secondary.testid} onClick={secondary.onClick} className="text-[11px] text-ink-400 hover:text-ink-100 px-2">{secondary.label}</button>
            </div>
        </div>
    );
}

function HoverTips({ disabled }) {
    const [tip, setTip] = useState(null); // {info, rect}
    const timerRef = useRef(null);
    useEffect(() => {
        if (disabled) { setTip(null); return; }
        const over = (e) => {
            const el = e.target.closest?.("[data-testid]");
            if (!el) return;
            const info = HOVER_TIPS[el.getAttribute("data-testid")];
            if (!info) return;
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                const r = el.getBoundingClientRect();
                setTip({ info, rect: { top: r.top, left: r.left, width: r.width, height: r.height } });
            }, 1200);
        };
        const out = (e) => {
            const el = e.target.closest?.("[data-testid]");
            if (el && HOVER_TIPS[el.getAttribute("data-testid")]) {
                if (timerRef.current) clearTimeout(timerRef.current);
                setTip(null);
            }
        };
        document.addEventListener("mouseover", over);
        document.addEventListener("mouseout", out);
        document.addEventListener("click", out, true);
        return () => {
            document.removeEventListener("mouseover", over);
            document.removeEventListener("mouseout", out);
            document.removeEventListener("click", out, true);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [disabled]);
    if (!tip) return null;
    const { info, rect } = tip;
    const w = 270;
    const below = rect.top + rect.height + 10;
    const flip = below + 150 > window.innerHeight;
    let left = Math.max(10, Math.min(rect.left, window.innerWidth - w - 10));
    return (
        <div data-testid="hover-tip" className="fixed z-[10035] pointer-events-none vd-tip-in"
            style={{ top: flip ? undefined : below, bottom: flip ? window.innerHeight - rect.top + 10 : undefined, left, width: w }}>
            <div className="bg-ink-950 border border-white/20 shadow-2xl p-3.5" style={{ borderLeftColor: "hsl(var(--gold))", borderLeftWidth: 2 }}>
                <div className="text-xs text-ink-50 font-medium">{info.name}</div>
                <p className="text-[11px] text-ink-400 mt-1">{info.desc}</p>
                <p className="text-[11px] text-gold/90 mt-1.5">✦ {info.benefit}</p>
                <p className="text-[10px] text-ink-500 mt-1.5 italic">💡 {info.tip}</p>
            </div>
        </div>
    );
}

function WelcomeModal({ onChoose }) {
    return (
        <div className="fixed inset-0 z-[10060]">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
            <div data-testid="onboarding-welcome" className="absolute inset-x-4 top-1/2 -translate-y-1/2 sm:inset-x-0 sm:mx-auto sm:max-w-lg bg-ink-950 border-2 p-8 sm:p-10 text-center vd-tip-in" style={{ borderColor: "hsl(var(--gold))" }}>
                <Sparkles size={30} className="text-gold mx-auto mb-5" />
                <h2 className="font-serif text-3xl text-ink-50">Benvenuto su VendiDirect.</h2>
                <p className="text-ink-400 text-sm mt-3 mb-8 leading-relaxed">
                    Ti accompagneremo passo dopo passo alla scoperta di tutte le funzionalità della piattaforma.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                    <button data-testid="welcome-start-tour" onClick={() => onChoose("tour")} className="cta-gold flex-1 py-3.5 text-sm flex items-center justify-center gap-2">
                        <PlayCircle size={16} /> Inizia Tour Guidato
                    </button>
                    <button data-testid="welcome-explore" onClick={() => onChoose("explore")} className="cta-outline flex-1 py-3.5 text-sm flex items-center justify-center gap-2">
                        <Compass size={16} /> Esplora Autonomamente
                    </button>
                </div>
                <p className="text-[10px] text-ink-500 mt-5">Potrai riaprire le guide in ogni momento dal pulsante "Guida" in alto.</p>
            </div>
        </div>
    );
}

function ResumeBanner({ tourId, onResume, onDismiss }) {
    return (
        <div data-testid="tour-resume-banner" className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[10040] bg-ink-950 border px-4 py-3 flex items-center gap-3 shadow-2xl vd-tip-in" style={{ borderColor: "hsl(var(--gold) / 0.5)" }}>
            <GraduationCap size={16} className="text-gold shrink-0" />
            <span className="text-xs text-ink-100">Vuoi continuare il tour <b className="text-gold">{MODULE_LABELS[tourId]}</b> da dove eri rimasto?</span>
            <button data-testid="tour-resume-yes" onClick={onResume} className="cta-gold px-3 py-1.5 text-[11px]">Riprendi</button>
            <button data-testid="tour-resume-no" onClick={onDismiss} className="text-ink-500 hover:text-ink-100"><X size={14} /></button>
        </div>
    );
}

export function OnboardingKick({ tab }) {
    const ob = useOnboarding();
    const tracked = useRef(null);
    useEffect(() => {
        if (!ob) return;
        ob.maybeAutoStart(TAB_TOURS[tab]);
        if (tracked.current !== tab) {
            tracked.current = tab;
            ob.trackVisit(tab);
        }
    }, [tab, ob]);
    return null;
}

export function OnboardingLauncher({ tab }) {
    const ob = useOnboarding();
    const [open, setOpen] = useState(false);
    if (!ob) return null;
    const tours = ob.state.tours || {};
    const pct = (id) => {
        const t = tours[id];
        if (!t) return null;
        if (t.status === "completed") return 100;
        return Math.min(99, Math.round(((t.step || 0) / (TOURS[id].steps.length || 1)) * 100));
    };
    const currentTour = TAB_TOURS[tab];
    return (
        <div className="relative">
            <button data-testid="onboarding-launcher" onClick={() => setOpen((o) => !o)}
                className="cta-outline px-3.5 py-2 text-xs flex items-center gap-1.5">
                <GraduationCap size={14} /> Guida
            </button>
            {open && (
                <div data-testid="onboarding-panel" className="absolute right-0 top-full mt-2 w-72 bg-ink-950 border border-white/15 shadow-2xl z-[10030] p-4 vd-tip-in">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-gold mb-3">Il tuo apprendimento</div>
                    <div className="space-y-2.5 mb-4 max-h-72 overflow-y-auto pr-1">
                        {Object.keys(TOURS).map((id) => {
                            const p = pct(id);
                            return (
                                <button key={id} data-testid={`module-progress-${id}`} onClick={() => { setOpen(false); ob.navigateAndStart(id); }}
                                    className="w-full text-left group">
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-ink-100 group-hover:text-gold transition-colors">{MODULE_LABELS[id]}</span>
                                        {p === 100 ? <span className="flex items-center gap-1 text-green-300 text-[10px]"><CheckCircle2 size={11} /> Completato</span>
                                            : p === null ? <span className="text-ink-500 text-[10px]">Non ancora esplorato</span>
                                                : <span className="text-gold text-[10px]">{p}%</span>}
                                    </div>
                                    <div className="h-1 bg-white/10 mt-1">
                                        <div className="h-1 transition-all" style={{ width: `${p || 0}%`, background: p === 100 ? "#22c55e" : "hsl(var(--gold))" }} />
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                    {currentTour && (
                        <button data-testid="show-me-this-page" onClick={() => { setOpen(false); ob.startTour(currentTour, 0); }}
                            className="cta-gold w-full py-2.5 text-xs flex items-center justify-center gap-1.5">
                            <Sparkles size={13} /> Mostrami questa pagina
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
